import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DatePicker } from '@gravity-ui/date-components';
import { dateTime } from '@gravity-ui/date-utils';
import {
  Button,
  Flex,
  Label,
  Progress,
  SegmentedRadioGroup,
  Select,
  Text,
} from '@gravity-ui/uikit';
import { config } from '@/shared/config';
import { useOrganizations } from '@/shared/auth';
import { useTranslation } from '@/shared/i18n';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoOrganizationSelectedState,
} from '@/shared/ui/states';
import { useObjects } from '@/features/objects/api';
import type { ObjectItem } from '@/features/objects/types';
import { useAccounts } from '@/features/accounts/api';
import type { Account } from '@/features/accounts/types';
import { MEALS } from '@/features/menu/types';
import { parseIsoDate, shiftIsoDate, todayIsoDate, toIsoDate } from '@/features/menu/dates';
import { formatDate, formatMoney } from '@/features/warehouse/format';
import { ALL_OBJECTS, collectStatistics, type DayPoint } from './aggregate';
import './statistics-route.css';

/** Быстрые окна периода; ручной выбор дат снимает выбор пресета. */
const PRESETS = [
  { value: 'days7', labelKey: 'statistics.preset7' },
  { value: 'days30', labelKey: 'statistics.preset30' },
  { value: 'month', labelKey: 'statistics.presetMonth' },
  { value: 'prevMonth', labelKey: 'statistics.presetPrevMonth' },
] as const;

type Preset = (typeof PRESETS)[number]['value'] | 'custom';

/** Первое число месяца, в который входит дата. */
function monthStart(iso: string): string {
  const date = parseIsoDate(iso);

  return toIsoDate(new Date(date.getFullYear(), date.getMonth(), 1));
}

/** Границы прошлого календарного месяца. */
function previousMonth(today: string): { from: string; to: string } {
  const date = parseIsoDate(today);
  const first = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  const last = new Date(date.getFullYear(), date.getMonth(), 0);

  return { from: toIsoDate(first), to: toIsoDate(last) };
}

/** Целое число с пробелами: «1 234». */
function formatPeople(value: number): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
}

/** Доля 0..1 в проценты: «34%». */
function formatShare(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/** Подпись дня на оси графика: «01.10». */
function axisLabel(date: string): string {
  return `${date.slice(8, 10)}.${date.slice(5, 7)}`;
}

/** Месяц «YYYY-MM» словами на языке интерфейса. */
function monthLabel(month: string, locale: string): string {
  const [year, monthIndex] = month.split('-').map(Number);
  const name = new Date(year, monthIndex - 1, 1).toLocaleDateString(locale, { month: 'long' });

  return `${name} ${year}`;
}

/** Карточка показателя: подпись, крупное значение и пояснение. */
function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="st-kpi">
      <Text variant="body-2" color="secondary">
        {label}
      </Text>
      <Text variant="header-2">{value}</Text>
      <Text variant="body-1" color="secondary">
        {hint}
      </Text>
    </div>
  );
}

/**
 * Столбики по дням. Нулевые дни остаются пустыми слотами — так видно, что в
 * этот день счёта не было, а не что людей не было.
 */
function DayChart({
  points,
  max,
  valueOf,
  format,
  emptyText,
}: {
  points: DayPoint[];
  max: number;
  valueOf: (point: DayPoint) => number;
  format: (value: number) => string;
  emptyText: string;
}) {
  if (points.length === 0 || max === 0) {
    return (
      <div className="st-chart__empty">
        <Text variant="body-2" color="secondary">
          {emptyText}
        </Text>
      </div>
    );
  }

  const labelStep = Math.max(1, Math.ceil(points.length / 10));

  return (
    <div className="st-chart-scroll">
      <div className="st-chart">
        {points.map((point) => (
          <div
            key={point.date}
            className="st-chart__slot"
            title={`${formatDate(point.date)} — ${format(valueOf(point))}`}
          >
            <div
              className={point.hasData ? 'st-chart__bar' : 'st-chart__bar st-chart__bar_empty'}
              style={{ height: `${(valueOf(point) / max) * 100}%` }}
            />
          </div>
        ))}
      </div>
      <div className="st-axis" aria-hidden>
        {points.map((point, index) => (
          <span key={point.date} className="st-axis__label">
            {index % labelStep === 0 ? axisLabel(point.date) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Страница «Статистика»: люди и расходы по счетам за выбранный период. */
export function StatisticsRoute() {
  const { current } = useOrganizations();
  const { t, i18n } = useTranslation();
  const objects = useObjects();
  const accounts = useAccounts();
  const [searchParams, setSearchParams] = useSearchParams();

  const [objectId, setObjectId] = useState(ALL_OBJECTS);
  const [from, setFrom] = useState(() => monthStart(todayIsoDate()));
  const [to, setTo] = useState(() => todayIsoDate());
  const [preset, setPreset] = useState<Preset>('month');

  const realAccounts = useMemo(() => accounts.data?.accounts ?? [], [accounts.data]);
  const realObjects = useMemo(() => objects.data?.objects ?? [], [objects.data]);

  // Демо-счета: в local включаются сами, если реальных за всё время нет;
  // ?demo=1 включает принудительно, ?demo=0 выключает. В проде — никогда.
  const demoParam = searchParams.get('demo');
  const demo =
    config.env !== 'prod' &&
    (demoParam === '1' ||
      (demoParam !== '0' &&
        config.env === 'local' &&
        accounts.isSuccess &&
        realAccounts.length === 0));
  // Генератор демо-счетов тянем отдельным чанком: в проде он не нужен вовсе.
  const [demoData, setDemoData] = useState<{ accounts: Account[]; objects: ObjectItem[] } | null>(
    null
  );
  useEffect(() => {
    if (!demo) {
      setDemoData(null);
      return;
    }

    let cancelled = false;
    void import('./demo-data')
      .then((module) => {
        if (cancelled) return;
        setDemoData({
          accounts: module.buildDemoAccounts(),
          objects: module.buildDemoObjects(),
        });
      })
      .catch(() => {
        // Чанк не загрузился — показываем пустое состояние вместо вечной загрузки.
        if (!cancelled) setDemoData({ accounts: [], objects: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [demo]);

  const dataAccounts = useMemo(
    () => (demo ? (demoData?.accounts ?? []) : realAccounts),
    [demo, demoData, realAccounts]
  );
  const dataObjects = useMemo(
    () => (demo ? (demoData?.objects ?? []) : realObjects),
    [demo, demoData, realObjects]
  );
  const demoLoading = demo && demoData === null;

  const objectOptions = useMemo(
    () =>
      dataObjects
        .filter((object) => !object.parentObjectId && object.type !== 'warehouse')
        .map((object) => ({ value: object.id, content: object.name })),
    [dataObjects]
  );

  const stats = useMemo(
    () =>
      collectStatistics(dataAccounts, dataObjects, {
        from,
        to,
        objectId: objectId === ALL_OBJECTS ? null : objectId,
      }),
    [dataAccounts, dataObjects, from, to, objectId]
  );

  const locale = i18n.language.startsWith('en') ? 'en-US' : 'ru-RU';
  const totals = stats.totals;
  const maxPeople = Math.max(0, ...stats.days.map((day) => day.people));
  const maxCharged = Math.max(0, ...stats.days.map((day) => day.charged));

  const applyPreset = (value: string) => {
    const today = todayIsoDate();
    setPreset(value as Preset);

    if (value === 'days7') {
      setFrom(shiftIsoDate(today, -6));
      setTo(today);
    } else if (value === 'days30') {
      setFrom(shiftIsoDate(today, -29));
      setTo(today);
    } else if (value === 'month') {
      setFrom(monthStart(today));
      setTo(today);
    } else if (value === 'prevMonth') {
      const range = previousMonth(today);
      setFrom(range.from);
      setTo(range.to);
    }
  };

  const changeFrom = (next: string) => {
    setPreset('custom');
    setFrom(next);
    if (next > to) setTo(next);
  };

  const changeTo = (next: string) => {
    setPreset('custom');
    setTo(next);
    if (next < from) setFrom(next);
  };

  const toggleDemo = () => {
    // Объекты демо и реальные не пересекаются — сбрасываем фильтр, иначе будет пусто.
    setObjectId(ALL_OBJECTS);
    setSearchParams(demo ? { demo: '0' } : { demo: '1' }, { replace: true });
  };

  if (current === null) {
    return <NoOrganizationSelectedState />;
  }

  return (
    <div className="st-page">
      <Flex className="st-toolbar" alignItems="center" gap={3} wrap>
        <Select
          size="l"
          width={280}
          label={t('statistics.objectLabel')}
          value={[objectId]}
          onUpdate={(value) => setObjectId(value[0] ?? ALL_OBJECTS)}
          options={[{ value: ALL_OBJECTS, content: t('statistics.allObjects') }, ...objectOptions]}
          disabled={!demo && objects.isPending}
        />
        <Flex alignItems="center" gap={1}>
          <DatePicker
            size="l"
            value={dateTime({ input: from })}
            onUpdate={(value) => {
              if (value) changeFrom(value.format('YYYY-MM-DD'));
            }}
            style={{ width: 170 }}
          />
          <Text variant="body-2" color="secondary">
            —
          </Text>
          <DatePicker
            size="l"
            value={dateTime({ input: to })}
            onUpdate={(value) => {
              if (value) changeTo(value.format('YYYY-MM-DD'));
            }}
            style={{ width: 170 }}
          />
        </Flex>
        <SegmentedRadioGroup size="l" value={preset} onUpdate={applyPreset}>
          {PRESETS.map((item) => (
            <SegmentedRadioGroup.Option key={item.value} value={item.value}>
              {t(item.labelKey)}
            </SegmentedRadioGroup.Option>
          ))}
        </SegmentedRadioGroup>
        {config.env !== 'prod' && (
          <Button view="outlined" size="l" onClick={toggleDemo}>
            {demo ? t('statistics.demoDisable') : t('statistics.demoEnable')}
          </Button>
        )}
        {demo && (
          <Label theme="info" size="m">
            {t('statistics.demoBadge')}
          </Label>
        )}
      </Flex>

      {demo && (
        <Text variant="body-1" color="secondary">
          {t('statistics.demoHint')}
        </Text>
      )}

      {demoLoading && <LoadingState />}
      {!demo && accounts.isPending && <LoadingState />}
      {!demo && accounts.isError && (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      )}
      {!demoLoading && (demo || accounts.isSuccess) && totals.daysWithData === 0 && (
        <EmptyState text={t('statistics.empty')} />
      )}

      {(demo || accounts.isSuccess) && !demoLoading && totals.daysWithData > 0 && (
        <>
          <div className="st-kpis">
            <Kpi
              label={t('statistics.kpiPeople')}
              value={formatPeople(totals.peopleDays)}
              hint={t('statistics.kpiPeopleHint', {
                value: formatPeople(Math.round(totals.avgPeoplePerDay)),
              })}
            />
            <Kpi
              label={t('statistics.kpiCharged')}
              value={formatMoney(totals.charged)}
              hint={t('statistics.kpiChargedHint', { value: formatMoney(totals.avgChargedPerDay) })}
            />
            <Kpi
              label={t('statistics.kpiPerPerson')}
              value={formatMoney(totals.costPerPerson)}
              hint={t('statistics.kpiPerPersonHint')}
            />
            <Kpi
              label={t('statistics.kpiDays')}
              value={`${formatPeople(totals.daysWithData)} / ${formatPeople(totals.days)}`}
              hint={t('statistics.kpiDaysHint', {
                percent: formatShare(totals.days > 0 ? totals.daysWithData / totals.days : 0),
              })}
            />
          </div>

          <div className="st-charts">
            <div className="st-card">
              <Flex className="st-card__head" justifyContent="space-between" gap={2} wrap>
                <Text variant="subheader-2">{t('statistics.chartPeople')}</Text>
                {maxPeople > 0 && (
                  <Text variant="body-2" color="secondary">
                    {t('statistics.maxValue', { value: formatPeople(maxPeople) })}
                  </Text>
                )}
              </Flex>
              <DayChart
                points={stats.days}
                max={maxPeople}
                valueOf={(point) => point.people}
                format={formatPeople}
                emptyText={t('statistics.chartEmpty')}
              />
            </div>

            <div className="st-card">
              <Flex className="st-card__head" justifyContent="space-between" gap={2} wrap>
                <Text variant="subheader-2">{t('statistics.chartCharged')}</Text>
                {maxCharged > 0 && (
                  <Text variant="body-2" color="secondary">
                    {t('statistics.maxValue', { value: formatMoney(maxCharged) })}
                  </Text>
                )}
              </Flex>
              <DayChart
                points={stats.days}
                max={maxCharged}
                valueOf={(point) => point.charged}
                format={formatMoney}
                emptyText={t('statistics.chartEmpty')}
              />
            </div>
          </div>

          <div className="st-split">
            <div className="st-card">
              <Flex className="st-card__head" justifyContent="space-between" gap={2} wrap>
                <Text variant="subheader-2">{t('statistics.byMonths')}</Text>
                {stats.months.length > 1 && (
                  <Text variant="body-2" color="secondary">
                    {t('statistics.avgPerMonth', {
                      value: formatMoney(totals.charged / stats.months.length),
                    })}
                  </Text>
                )}
              </Flex>
              <div className="st-table-wrap">
                <table className="st-table">
                  <thead>
                    <tr>
                      <th>{t('statistics.colMonth')}</th>
                      <th className="st-num">{t('statistics.colPeopleDays')}</th>
                      <th className="st-num">{t('statistics.colCharged')}</th>
                      <th className="st-num">{t('statistics.colPerDay')}</th>
                      <th className="st-num">{t('statistics.colPerPerson')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.months.map((month) => (
                      <tr key={month.month}>
                        <td>{monthLabel(month.month, locale)}</td>
                        <td className="st-num">{formatPeople(month.peopleDays)}</td>
                        <td className="st-num">{formatMoney(month.charged)}</td>
                        <td className="st-num">{formatMoney(month.avgChargedPerDay)}</td>
                        <td className="st-num">{formatMoney(month.costPerPerson)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="st-card">
              <Text variant="subheader-2" className="st-card__title">
                {t('statistics.byObjects')}
              </Text>
              <div className="st-table-wrap">
                <table className="st-table">
                  <thead>
                    <tr>
                      <th>{t('statistics.colObject')}</th>
                      <th className="st-num">{t('statistics.colPeopleDays')}</th>
                      <th className="st-num">{t('statistics.colCharged')}</th>
                      <th className="st-num">{t('statistics.colPerPerson')}</th>
                      <th className="st-num">{t('statistics.colShare')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.objects.map((object) => (
                      <tr key={object.objectId}>
                        <td className="st-name" title={object.name}>
                          {object.name}
                        </td>
                        <td className="st-num">{formatPeople(object.peopleDays)}</td>
                        <td className="st-num">{formatMoney(object.charged)}</td>
                        <td className="st-num">{formatMoney(object.costPerPerson)}</td>
                        <td className="st-num">{formatShare(object.share)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="st-card">
            <Flex className="st-card__head" justifyContent="space-between" gap={2} wrap>
              <Text variant="subheader-2">{t('statistics.byDays')}</Text>
              {totals.daysWithData < totals.days && (
                <Text variant="body-2" color="secondary">
                  {t('statistics.daysWithoutData', {
                    count: totals.days - totals.daysWithData,
                  })}
                </Text>
              )}
            </Flex>
            <div className="st-table-wrap st-table-wrap_days">
              <table className="st-table">
                <thead>
                  <tr>
                    <th>{t('statistics.colDate')}</th>
                    <th className="st-num">{t('statistics.colPeople')}</th>
                    <th className="st-num">{t('statistics.colCharged')}</th>
                    <th className="st-num">{t('statistics.colPerPerson')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...stats.days]
                    .reverse()
                    .filter((day) => day.hasData)
                    .map((day) => (
                      <tr key={day.date}>
                        <td>{formatDate(day.date)}</td>
                        <td className="st-num">{formatPeople(day.people)}</td>
                        <td className="st-num">{formatMoney(day.charged)}</td>
                        <td className="st-num">
                          {day.people > 0 ? formatMoney(day.charged / day.people) : '—'}
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{t('statistics.total')}</td>
                    <td className="st-num">{formatPeople(totals.peopleDays)}</td>
                    <td className="st-num">{formatMoney(totals.charged)}</td>
                    <td className="st-num">{formatMoney(totals.costPerPerson)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {(stats.meals.length > 0 || stats.diets.length > 0) && (
            <div className="st-split">
              {stats.meals.length > 0 && (
                <div className="st-card">
                  <Text variant="subheader-2" className="st-card__title">
                    {t('statistics.byMeals')}
                  </Text>
                  <div className="st-rows">
                    {stats.meals.map((meal) => {
                      const definition = MEALS.find((item) => item.id === meal.mealId);

                      return (
                        <div key={meal.mealId} className="st-row">
                          <Text variant="body-2">
                            {definition ? t(definition.labelKey) : meal.mealId}
                          </Text>
                          <Text variant="body-2" color="secondary">
                            {t('statistics.portionsValue', {
                              value: formatPeople(meal.portions),
                            })}
                          </Text>
                          <Text variant="body-2">{formatMoney(meal.charged)}</Text>
                          <Progress
                            className="st-row__bar"
                            value={Math.round(meal.share * 100)}
                            theme="success"
                            size="xs"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {stats.diets.length > 0 && (
                <div className="st-card">
                  <Text variant="subheader-2" className="st-card__title">
                    {t('statistics.byDiets')}
                  </Text>
                  <div className="st-rows">
                    {stats.diets.map((diet) => (
                      <div key={diet.id} className="st-row">
                        <Text variant="body-2" className="st-name" title={diet.name}>
                          {diet.name}
                        </Text>
                        <Text variant="body-2" color="secondary">
                          {t('statistics.portionsValue', {
                            value: formatPeople(diet.portions),
                          })}
                        </Text>
                        <Text variant="body-2">{formatMoney(diet.charged)}</Text>
                        <Progress
                          className="st-row__bar"
                          value={Math.round(diet.share * 100)}
                          theme="success"
                          size="xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
